import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AppLayout from "../components/AppLayout";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import ReportButton from "../components/ReportButton";
import { MessageSquare, Send, ShieldCheck, ShieldHalf, Plus, X, ArrowLeft, Check, CheckCheck, SmilePlus, UserPlus, Users, Search, Pencil, LogOut } from "lucide-react";
import PresenceDot from "../components/PresenceDot";
import UserNameLink from "../components/UserNameLink";

function timeAgo(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  if (isNaN(diff)) return "";
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function UserBubble({ u, size = 32 }) {
  const letter = (u?.name || "?").trim()[0]?.toUpperCase() || "?";
  const isAdmin = u?.role === "admin";
  const isMod = u?.role === "moderator";
  const style = { width: size, height: size };
  if (u?.avatar_url) {
    return <img src={u.avatar_url} alt={u.name} style={style} className={`object-cover border shrink-0 ${isAdmin ? "border-[#0D9488]/60" : isMod ? "border-emerald-600/60" : "border-slate-300"}`} />;
  }
  return (
    <div style={style} className={`flex items-center justify-center border font-display font-black shrink-0 ${
      isAdmin ? "border-[#0D9488]/60 bg-[#0D9488]/10 text-[#0D9488]"
      : isMod ? "border-emerald-600/60 bg-emerald-950/40 text-emerald-400"
      : "border-slate-300 bg-slate-50 text-slate-800"
    }`}>
      {letter}
    </div>
  );
}

const QUICK_REACTIONS = ["👍", "❤️", "😂", "🎉", "🔥", "👀"];

/**
 * Renders up to 3 avatars overlapping (Slack-style) with a "+N" bubble for the
 * rest. Used in group thread rows and the group header to give a quick visual
 * cue that a thread has multiple participants without spelling out every name.
 */
function StackedAvatars({ users, size = 32 }) {
  const shown = users.slice(0, 3);
  const rest = Math.max(0, users.length - 3);
  const overlap = Math.round(size * 0.35);
  return (
    <div className="relative flex items-center shrink-0" style={{ height: size, width: size + overlap * (shown.length - 1) + (rest ? overlap : 0) }} data-testid="messages-stacked-avatars">
      {shown.map((u, i) => (
        <div key={u.id || i} className="absolute" style={{ left: i * overlap, zIndex: 10 - i }}>
          <UserBubble u={u} size={size} />
        </div>
      ))}
      {rest > 0 && (
        <div
          className="absolute flex items-center justify-center border border-slate-300 bg-slate-100 text-slate-700 font-display font-black text-[10px]"
          style={{ left: shown.length * overlap, width: size, height: size, zIndex: 1 }}
        >
          +{rest}
        </div>
      )}
    </div>
  );
}

function ReadReceipt({ mine, message, totalOthers = 1 }) {
  if (!mine) return null;
  // Group-aware receipt: show "Read by N of M" when the thread has 2+ others,
  // fall back to a boolean Read/Sent chip for 1:1s.
  if (totalOthers > 1) {
    const readN = message.read_by_others_count || 0;
    const allRead = readN >= totalOthers;
    const Icon = allRead ? CheckCheck : (readN > 0 ? CheckCheck : Check);
    return (
      <span
        className={`inline-flex items-center gap-1 ${allRead ? "text-[#0D9488]" : readN > 0 ? "text-slate-600" : "text-slate-400"}`}
        title={allRead ? "Read by everyone" : `Read by ${readN} of ${totalOthers}`}
        data-testid={`dm-read-receipt-${message.id}`}
      >
        <Icon className="w-3 h-3" strokeWidth={2.5} />
        <span className="font-mono-num text-[9px] uppercase tracking-widest">
          {allRead ? "Read by all" : readN > 0 ? `Read by ${readN} of ${totalOthers}` : "Sent"}
        </span>
      </span>
    );
  }
  const read = !!message.read_by_other;
  const Icon = read ? CheckCheck : Check;
  return (
    <span
      className={`inline-flex items-center gap-1 ${read ? "text-[#0D9488]" : "text-slate-400"}`}
      title={read && message.read_at_other ? `Read ${timeAgo(message.read_at_other)}` : "Sent"}
      data-testid={`dm-read-receipt-${message.id}`}
    >
      <Icon className="w-3 h-3" strokeWidth={2.5} />
      <span className="font-mono-num text-[9px] uppercase tracking-widest">{read ? "Read" : "Sent"}</span>
    </span>
  );
}

function ReactionPill({ reaction, onToggle, disabled }) {
  return (
    <button
      type="button"
      onClick={() => onToggle(reaction.emoji)}
      disabled={disabled}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 border text-[11px] leading-none transition-colors ${
        reaction.mine
          ? "border-[#0D9488]/60 bg-[#0D9488]/10 text-[#0D9488]"
          : "border-slate-300 bg-slate-50 text-slate-800 hover:border-zinc-500"
      } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
      data-testid={`dm-reaction-pill-${reaction.emoji}`}
    >
      <span>{reaction.emoji}</span>
      <span className="font-mono-num text-[9px]">{reaction.count}</span>
    </button>
  );
}

function ReactionPicker({ onPick, onClose }) {
  return (
    <div
      className="absolute -top-9 right-0 z-10 flex items-center gap-1 px-1.5 py-1 border border-slate-300 bg-white shadow-lg"
      onMouseLeave={onClose}
      data-testid="dm-reaction-picker"
    >
      {QUICK_REACTIONS.map((e) => (
        <button
          key={e}
          type="button"
          onClick={() => { onPick(e); onClose(); }}
          className="text-base leading-none px-1 py-0.5 hover:bg-slate-200 transition-colors"
          data-testid={`dm-reaction-pick-${e}`}
          aria-label={`React with ${e}`}
        >
          {e}
        </button>
      ))}
    </div>
  );
}

function RolePill({ role }) {
  if (role === "admin") return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-[#0D9488]/40 text-[#0D9488] font-mono-num text-[9px] uppercase tracking-widest">
      <ShieldCheck className="w-2.5 h-2.5" /> Admin
    </span>
  );
  if (role === "moderator") return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-emerald-700/60 text-emerald-400 font-mono-num text-[9px] uppercase tracking-widest">
      <ShieldHalf className="w-2.5 h-2.5" /> Mod
    </span>
  );
  return null;
}

export default function Messages() {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [threadDetail, setThreadDetail] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  /** Picker mode: 'new' opens a new thread with the chosen member. 'add' adds
   *  that member to the currently open thread as a new participant. */
  const [pickerMode, setPickerMode] = useState("new");
  const [pickerQuery, setPickerQuery] = useState("");
  const [pickerResults, setPickerResults] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [addingParticipant, setAddingParticipant] = useState(false);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  // Message search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null); // null = not searching, [] = no results
  const [searchLoading, setSearchLoading] = useState(false);
  // Rename dialog for group threads
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [renameBusy, setRenameBusy] = useState(false);
  // Leave-group confirmation
  const [leaveBusy, setLeaveBusy] = useState(false);
  const scrollRef = useRef(null);
  const [params] = useSearchParams();
  const [pickerFor, setPickerFor] = useState(null); // message id whose picker is open

  const loadThreads = useCallback(async () => {
    try {
      const { data } = await api.get("/messages/threads");
      setThreads(data.items || []);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, []);

  // Search the member directory (excluding people already in the current thread).
  const runDirectorySearch = useCallback(async (q, excludeIds = []) => {
    setPickerLoading(true);
    try {
      const excl = excludeIds.filter(Boolean).join(",");
      const { data } = await api.get(`/messages/directory`, { params: { q, exclude: excl } });
      setPickerResults(data || []);
    } catch (err) {
      setPickerResults([]);
      toast.error(formatApiError(err));
    } finally {
      setPickerLoading(false);
    }
  }, []);

  const openNewThreadPicker = () => {
    setPickerMode("new");
    setPickerQuery("");
    setPickerResults([]);
    setShowPicker(true);
    runDirectorySearch("", []);
  };

  const openAddParticipantPicker = () => {
    if (!threadDetail) return;
    setPickerMode("add");
    setPickerQuery("");
    setPickerResults([]);
    setShowPicker(true);
    const alreadyIn = (threadDetail.participants || []).map((p) => p.id);
    runDirectorySearch("", alreadyIn);
  };

  const closePicker = () => {
    setShowPicker(false);
    setPickerQuery("");
    setPickerResults([]);
  };

  const openThread = useCallback(async (id) => {
    setSelectedId(id);
    try {
      const { data } = await api.get(`/messages/threads/${id}`);
      setThreadDetail(data);
      // Mark read
      await api.post(`/messages/threads/${id}/read`);
      setThreads((cur) => cur.map((t) => t.id === id ? { ...t, unread_count: 0 } : t));
      setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }), 100);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, []);

  useEffect(() => { loadThreads(); }, [loadThreads]);

  // Debounced directory search when the picker query changes.
  useEffect(() => {
    if (!showPicker) return;
    const excl = pickerMode === "add" && threadDetail
      ? (threadDetail.participants || []).map((p) => p.id)
      : [];
    const t = setTimeout(() => runDirectorySearch(pickerQuery, excl), 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickerQuery, showPicker, pickerMode, threadDetail?.thread_id]);

  // Poll active thread — fast (3s) so typing indicators and new messages feel live.
  useEffect(() => {
    if (!selectedId) return;
    const t = setInterval(() => {
      api.get(`/messages/threads/${selectedId}`).then(({ data }) => {
        setThreadDetail(data);
      }).catch(() => {});
    }, 3000);
    return () => clearInterval(t);
  }, [selectedId]);

  // If ?thread= param present, open that thread
  useEffect(() => {
    const q = params.get("thread");
    if (q && q !== selectedId) openThread(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const startWithMember = async (member) => {
    try {
      const { data } = await api.post("/messages/threads", { other_user_id: member.id });
      closePicker();
      await loadThreads();
      openThread(data.id);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const addParticipant = async (member) => {
    if (!threadDetail || addingParticipant) return;
    setAddingParticipant(true);
    try {
      const { data } = await api.post(`/messages/threads/${threadDetail.thread_id}/participants`, { user_id: member.id });
      toast.success(`${member.name} was added to the conversation`);
      closePicker();
      // Refresh thread + list so the new participant + system message show up.
      const { data: fresh } = await api.get(`/messages/threads/${threadDetail.thread_id}`);
      setThreadDetail(fresh);
      loadThreads();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAddingParticipant(false);
    }
  };

  // ---------- Rename group ----------
  const openRename = () => {
    if (!threadDetail?.is_group) return;
    setRenameValue(threadDetail.name || "");
    setRenameOpen(true);
  };
  const submitRename = async () => {
    if (!threadDetail || renameBusy) return;
    setRenameBusy(true);
    try {
      const trimmed = renameValue.trim();
      await api.patch(`/messages/threads/${threadDetail.thread_id}`, { name: trimmed });
      const { data: fresh } = await api.get(`/messages/threads/${threadDetail.thread_id}`);
      setThreadDetail(fresh);
      loadThreads();
      setRenameOpen(false);
      toast.success(trimmed ? "Renamed conversation" : "Cleared group name");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRenameBusy(false);
    }
  };

  // ---------- Leave group ----------
  const leaveGroup = async () => {
    if (!threadDetail?.is_group || leaveBusy) return;
    const confirmed = window.confirm("Leave this conversation? You won't see new messages here anymore.");
    if (!confirmed) return;
    setLeaveBusy(true);
    try {
      await api.delete(`/messages/threads/${threadDetail.thread_id}/leave`);
      toast.success("You left the conversation");
      setSelectedId(null);
      setThreadDetail(null);
      loadThreads();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLeaveBusy(false);
    }
  };

  // ---------- Message search across all threads ----------
  const runMessageSearch = useCallback(async (q) => {
    const query = (q || "").trim();
    if (query.length < 2) { setSearchResults(null); return; }
    setSearchLoading(true);
    try {
      const { data } = await api.get(`/messages/search`, { params: { q: query } });
      setSearchResults(data.items || []);
    } catch (err) {
      toast.error(formatApiError(err));
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => runMessageSearch(searchQuery), 250);
    return () => clearTimeout(t);
  }, [searchQuery, runMessageSearch]);

  const clearSearch = () => { setSearchQuery(""); setSearchResults(null); };

  const send = async () => {
    if (!body.trim() || !selectedId) return;
    setSending(true);
    try {
      const { data } = await api.post(`/messages/threads/${selectedId}`, { body: body.trim() });
      setThreadDetail((cur) => cur ? { ...cur, messages: [...cur.messages, data] } : cur);
      setBody("");
      // Clear my typing heartbeat immediately so recipients stop seeing "…is typing".
      lastTypingSentRef.current = 0;
      try { await api.delete(`/messages/threads/${selectedId}/typing`); } catch { /* best-effort */ }
      // Refresh threads list to update preview
      loadThreads();
      setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }), 50);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  // Typing heartbeat — fire at most every ~2.5s while user is actively typing.
  const lastTypingSentRef = useRef(0);
  const notifyTyping = useCallback(async () => {
    if (!selectedId) return;
    const now = Date.now();
    if (now - lastTypingSentRef.current < 2500) return;
    lastTypingSentRef.current = now;
    try {
      await api.post(`/messages/threads/${selectedId}/typing`);
    } catch { /* best-effort — never let a failed heartbeat interrupt UX */ }
  }, [selectedId]);

  const toggleReaction = async (messageId, emoji) => {
    // Optimistic toggle
    setThreadDetail((cur) => {
      if (!cur) return cur;
      return {
        ...cur,
        messages: cur.messages.map((m) => {
          if (m.id !== messageId) return m;
          const existing = m.reactions.find((r) => r.emoji === emoji);
          let next;
          if (existing) {
            if (existing.mine && existing.count === 1) {
              next = m.reactions.filter((r) => r.emoji !== emoji);
            } else {
              next = m.reactions.map((r) => r.emoji === emoji
                ? { ...r, count: r.mine ? r.count - 1 : r.count + 1, mine: !r.mine }
                : r);
            }
          } else {
            next = [...m.reactions, { emoji, count: 1, mine: true }];
          }
          return { ...m, reactions: next };
        }),
      };
    });
    try {
      const { data } = await api.post(`/messages/messages/${messageId}/react`, { emoji });
      // Reconcile with server truth
      setThreadDetail((cur) => cur ? {
        ...cur,
        messages: cur.messages.map((m) => m.id === messageId ? { ...m, reactions: data.reactions } : m),
      } : cur);
    } catch (err) {
      toast.error(formatApiError(err));
      // Fall back to refetch on failure
      if (selectedId) {
        try {
          const { data } = await api.get(`/messages/threads/${selectedId}`);
          setThreadDetail(data);
        } catch (refetchErr) {
          // Refetch failed too — user already saw the toast for the original error.
          // Log so this is visible in browser devtools without spawning a second toast.
          console.error("[DM] reaction refetch failed:", refetchErr);
        }
      }
    }
  };

  return (
    <AppLayout>
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <MessageSquare className="w-4 h-4 text-[#0D9488]" />
          <div className="font-mono-num text-[11px] text-[#0D9488] uppercase tracking-[0.3em]">// Direct Messages</div>
        </div>
        <h1 className="font-display font-black uppercase tracking-tight text-4xl lg:text-5xl leading-none">Messages</h1>
        <p className="text-slate-500 mt-3 text-sm">
          Chat 1:1 with any member — and when a conversation needs a third voice, tap <span className="font-semibold text-slate-800">Add someone</span> to loop them in.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4 border border-slate-200 bg-white min-h-[70vh]">
        {/* Left: Thread list */}
        <div className={`border-r border-slate-200 flex flex-col ${selectedId ? "hidden lg:flex" : "flex"}`}>
          <div className="p-3 border-b border-slate-200 flex items-center justify-between">
            <div className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-slate-500">// Conversations ({threads.length})</div>
            <Button
              type="button"
              size="sm"
              onClick={openNewThreadPicker}
              className="h-7 rounded-none bg-[#0D9488] hover:bg-[#0F766E] text-white uppercase tracking-widest text-[10px] font-semibold px-2"
              data-testid="messages-new-thread"
            >
              <Plus className="w-3 h-3 mr-1" /> New
            </Button>
          </div>
          {/* Search bar */}
          <div className="p-2 border-b border-slate-200 relative">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search all messages…"
              className="bg-slate-50 border-slate-200 rounded-none h-9 pl-8 pr-8 text-sm"
              data-testid="messages-search-input"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                aria-label="Clear search"
                data-testid="messages-search-clear"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <div className="flex-1 overflow-y-auto">
            {searchResults !== null ? (
              // Search results replace the thread list while a query is active.
              searchLoading ? (
                <div className="p-6 text-center text-slate-500 text-xs font-mono-num uppercase tracking-widest" data-testid="messages-search-loading">Searching…</div>
              ) : searchResults.length === 0 ? (
                <div className="p-6 text-center text-slate-500 text-sm" data-testid="messages-search-empty">
                  <MessageSquare className="w-6 h-6 mx-auto mb-2 text-slate-400" />
                  <div className="font-mono-num text-[10px] uppercase tracking-widest">No matches</div>
                  <div className="text-xs mt-1">Try a shorter or different search term.</div>
                </div>
              ) : (
                <div data-testid="messages-search-results">
                  <div className="px-3 py-2 border-b border-slate-200 text-[10px] font-mono-num uppercase tracking-widest text-slate-500">
                    {searchResults.length} match{searchResults.length === 1 ? "" : "es"}
                  </div>
                  {searchResults.map((r) => {
                    const title = r.is_group
                      ? (r.thread_name || (r.participants || []).map((p) => p.name).join(", "))
                      : (r.participants[0]?.name || "Unknown");
                    // Highlight the query in the snippet.
                    const q = searchQuery.trim();
                    let bodyHtml = r.snippet;
                    if (q) {
                      const esc = q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
                      bodyHtml = r.snippet.replace(new RegExp(`(${esc})`, "gi"), '<mark class="bg-[#0D9488]/20 text-slate-900 px-0.5">$1</mark>');
                    }
                    return (
                      <button
                        key={r.message_id}
                        onClick={() => { clearSearch(); openThread(r.thread_id); }}
                        className="w-full text-left px-3 py-3 border-b border-slate-200 hover:bg-slate-50 transition-colors flex flex-col gap-1"
                        data-testid={`messages-search-result-${r.message_id}`}
                      >
                        <div className="flex items-center gap-1.5 text-slate-900 font-medium text-sm truncate">
                          {r.is_group && <Users className="w-3 h-3 text-[#0D9488] shrink-0" />}
                          <span className="truncate">{title}</span>
                          <span className="ml-auto text-[9px] font-mono-num text-slate-400 shrink-0">{timeAgo(r.created_at)}</span>
                        </div>
                        <div className="text-[10px] font-mono-num uppercase tracking-widest text-[#0D9488]">
                          {r.sender?.is_self ? "You" : (r.sender?.name || "System")}
                        </div>
                        <div className="text-xs text-slate-600 line-clamp-2" dangerouslySetInnerHTML={{ __html: bodyHtml }} />
                      </button>
                    );
                  })}
                </div>
              )
            ) : threads.length === 0 ? (
              <div className="p-6 text-center text-slate-500 text-sm">
                <MessageSquare className="w-6 h-6 mx-auto mb-2 text-slate-400" />
                <div className="font-mono-num text-[10px] uppercase tracking-widest">No conversations yet</div>
                <div className="text-xs mt-1">Click NEW to start a chat with any member.</div>
              </div>
            ) : threads.map((t) => {
              const parts = t.participants || (t.other_user ? [t.other_user] : []);
              const isGroup = !!t.is_group;
              const primary = parts[0];
              return (
                <button
                  key={t.id}
                  onClick={() => openThread(t.id)}
                  className={`w-full text-left px-3 py-3 border-b border-slate-200 flex items-start gap-3 hover:bg-slate-50 transition-colors ${
                    selectedId === t.id ? "bg-slate-50" : ""
                  }`}
                  data-testid={`messages-thread-${t.id}`}
                >
                  {isGroup ? (
                    <StackedAvatars users={parts} size={36} />
                  ) : (
                    <UserNameLink userId={primary?.id} handle={primary?.handle} name={primary?.name} testid={`messages-thread-avatar-link-${t.id}`}>
                      <UserBubble u={primary} size={36} />
                    </UserNameLink>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {!isGroup && <PresenceDot userId={primary?.id} />}
                      {isGroup ? (
                        <div className="flex items-center gap-1.5 text-sm text-slate-900 font-medium truncate">
                          <Users className="w-3.5 h-3.5 text-[#0D9488] shrink-0" />
                          <span className="truncate">{t.name || parts.map((p) => p.name).join(", ")}</span>
                        </div>
                      ) : (
                        <>
                          <UserNameLink userId={primary?.id} handle={primary?.handle} name={primary?.name} className="text-sm text-slate-900 font-medium truncate" showUnderline={false} testid={`messages-thread-name-link-${t.id}`} />
                          <RolePill role={primary?.role} />
                        </>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 truncate mt-0.5">
                      {t.last_message_preview || "No messages yet"}
                    </div>
                    <div className="font-mono-num text-[9px] uppercase tracking-widest text-slate-400 mt-1">
                      {timeAgo(t.last_message_at)}
                      {isGroup && <span className="ml-2 text-[#0D9488]">· {parts.length + 1} people</span>}
                    </div>
                  </div>
                  {t.unread_count > 0 && (
                    <span className="bg-[#0D9488] text-white text-[10px] font-mono-num font-bold px-1.5 py-0.5" data-testid={`messages-unread-${t.id}`}>
                      {t.unread_count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Right: Thread pane */}
        <div className={`flex flex-col min-h-[70vh] ${!selectedId ? "hidden lg:flex" : "flex"}`}>
          {!threadDetail ? (
            <div className="flex-1 flex items-center justify-center text-slate-500 text-sm">
              Select a conversation to start chatting
            </div>
          ) : (
            <>
              {/* Header — supports both 1:1 and group. Add-someone button in the corner. */}
              <div className="p-3 border-b border-slate-200 flex items-start gap-3">
                <button className="lg:hidden text-slate-600 hover:text-slate-900 mt-1" onClick={() => { setSelectedId(null); setThreadDetail(null); }} aria-label="Back">
                  <ArrowLeft className="w-4 h-4" />
                </button>
                {threadDetail.is_group ? (
                  <StackedAvatars users={threadDetail.participants || []} size={36} />
                ) : (
                  <UserNameLink userId={threadDetail.other_user?.id} handle={threadDetail.other_user?.handle} name={threadDetail.other_user?.name} testid="messages-thread-head-avatar-link">
                    <UserBubble u={threadDetail.other_user} size={36} />
                  </UserNameLink>
                )}
                <div className="flex-1 min-w-0">
                  {threadDetail.is_group ? (
                    <>
                      <div className="flex items-center gap-1.5" data-testid="messages-thread-head-group">
                        <Users className="w-3.5 h-3.5 text-[#0D9488] shrink-0" />
                        <span className="text-sm text-slate-900 font-semibold truncate" data-testid="messages-thread-head-title">
                          {threadDetail.name || (threadDetail.participants || []).map((p) => p.name).join(", ")}
                        </span>
                        <button
                          type="button"
                          onClick={openRename}
                          className="text-slate-400 hover:text-[#0D9488] transition-colors shrink-0"
                          title={threadDetail.name ? "Rename group" : "Name this group"}
                          data-testid="messages-rename-group"
                        >
                          <Pencil className="w-3 h-3" />
                        </button>
                      </div>
                      <div className="text-[10px] font-mono-num text-slate-500 uppercase tracking-widest mt-0.5">
                        Group chat · {(threadDetail.participants?.length || 0) + 1} people
                        {threadDetail.name && <span className="text-slate-400"> · {(threadDetail.participants || []).map((p) => p.name).join(", ")}</span>}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-2">
                        <PresenceDot userId={threadDetail.other_user?.id} />
                        <UserNameLink userId={threadDetail.other_user?.id} handle={threadDetail.other_user?.handle} name={threadDetail.other_user?.name} className="text-sm text-slate-900 font-semibold truncate" showUnderline={false} testid="messages-thread-head-name-link" />
                        <RolePill role={threadDetail.other_user?.role} />
                      </div>
                      <div className="text-[10px] font-mono-num text-slate-500 uppercase tracking-widest">
                        {threadDetail.other_user?.email}
                      </div>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={openAddParticipantPicker}
                    disabled={addingParticipant}
                    className="inline-flex items-center gap-1.5 h-8 px-2.5 border border-slate-200 hover:border-[#0D9488] hover:text-[#0D9488] text-slate-700 font-mono-num text-[10px] uppercase tracking-widest transition-colors disabled:opacity-50"
                    data-testid="messages-add-participant"
                    title="Loop someone else into this conversation"
                  >
                    <UserPlus className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Add someone</span>
                  </button>
                  {threadDetail.is_group && (
                    <button
                      type="button"
                      onClick={leaveGroup}
                      disabled={leaveBusy}
                      className="inline-flex items-center gap-1.5 h-8 px-2.5 border border-slate-200 hover:border-[#FF3B30] hover:text-[#FF3B30] text-slate-700 font-mono-num text-[10px] uppercase tracking-widest transition-colors disabled:opacity-50"
                      data-testid="messages-leave-group"
                      title="Leave this conversation"
                    >
                      <LogOut className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">{leaveBusy ? "Leaving…" : "Leave"}</span>
                    </button>
                  )}
                </div>
              </div>
              {/* Messages */}
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
                {threadDetail.messages.length === 0 ? (
                  <div className="text-center text-slate-500 text-sm py-8">
                    No messages yet — say hi to break the ice.
                  </div>
                ) : threadDetail.messages.map((m) => {
                  // System messages render centered/italic so users recognize
                  // them as thread events (e.g. add-participant), not chat.
                  if (m.system) {
                    return (
                      <div key={m.id} className="flex justify-center" data-testid={`messages-system-${m.id}`}>
                        <div className="text-[10px] font-mono-num uppercase tracking-widest text-slate-500 bg-slate-50 border border-slate-200 px-3 py-1">
                          <Users className="w-3 h-3 inline-block mr-1.5 -mt-0.5 text-[#0D9488]" />
                          {m.body}
                        </div>
                      </div>
                    );
                  }
                  const mine = m.sender_id === user?.id;
                  const hasReactions = (m.reactions?.length || 0) > 0;
                  // In group threads, show the sender name above their bubble
                  // so it's clear who's talking (irrelevant in 1:1).
                  const senderInGroup = !mine && threadDetail.is_group
                    ? (threadDetail.participants || []).find((p) => p.id === m.sender_id)
                    : null;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"} group`} data-testid={`messages-msg-${m.id}`}>
                      <div className={`relative max-w-[75%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
                        {senderInGroup && (
                          <div className="text-[10px] font-mono-num uppercase tracking-widest text-slate-500 mb-0.5 ml-1" data-testid={`messages-sender-${m.id}`}>
                            {senderInGroup.name}
                          </div>
                        )}
                        <div className={`relative px-3 py-2 border ${
                          mine ? "border-[#0D9488]/40 bg-[#0D9488]/5 text-slate-900"
                               : "border-slate-200 bg-slate-50 text-slate-900"
                        }`}>
                          {/* Hover actions: report (not-mine only) + emoji reaction */}
                          <div className={`absolute -top-3 ${mine ? "-left-3" : "-right-3"} opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1`}>
                            {!mine && (
                              <div className="border border-slate-300 bg-white p-0.5" title="Report this message">
                                <ReportButton targetType="dm_message" targetId={m.id} compact testid={`dm-report-${m.id}`} />
                              </div>
                            )}
                            <button
                              type="button"
                              onClick={() => setPickerFor(pickerFor === m.id ? null : m.id)}
                              className="w-7 h-7 border border-slate-300 bg-white text-slate-600 hover:text-[#0D9488] hover:border-[#0D9488]/60 flex items-center justify-center"
                              aria-label="Add reaction"
                              data-testid={`dm-add-reaction-${m.id}`}
                            >
                              <SmilePlus className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          {pickerFor === m.id && (
                            <ReactionPicker
                              onPick={(e) => toggleReaction(m.id, e)}
                              onClose={() => setPickerFor(null)}
                            />
                          )}
                          <div className="text-sm whitespace-pre-wrap leading-relaxed">{m.body}</div>
                          <div className="flex items-center justify-between gap-3 mt-1">
                            <div className="font-mono-num text-[9px] uppercase tracking-widest text-slate-500">
                              {timeAgo(m.created_at)}
                            </div>
                            <ReadReceipt mine={mine} message={m} totalOthers={threadDetail.participants?.length || 1} />
                          </div>
                        </div>
                        {hasReactions && (
                          <div className={`mt-1 flex flex-wrap gap-1 ${mine ? "justify-end" : "justify-start"}`} data-testid={`dm-reactions-${m.id}`}>
                            {m.reactions.map((r) => (
                              <ReactionPill
                                key={r.emoji}
                                reaction={r}
                                onToggle={(e) => toggleReaction(m.id, e)}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                {(threadDetail.typing || []).length > 0 && (
                  <div className="flex justify-start" data-testid="messages-typing">
                    <div className="inline-flex items-center gap-2 px-3 py-2 border border-slate-200 bg-slate-50">
                      <span className="typing-dots inline-flex gap-0.5" aria-hidden>
                        <span className="w-1.5 h-1.5 rounded-full bg-[#0D9488] inline-block animate-bounce" style={{ animationDelay: "0ms" }} />
                        <span className="w-1.5 h-1.5 rounded-full bg-[#0D9488] inline-block animate-bounce" style={{ animationDelay: "150ms" }} />
                        <span className="w-1.5 h-1.5 rounded-full bg-[#0D9488] inline-block animate-bounce" style={{ animationDelay: "300ms" }} />
                      </span>
                      <span className="text-[11px] font-mono-num uppercase tracking-widest text-slate-600">
                        {threadDetail.typing.length === 1
                          ? `${threadDetail.typing[0].name} is typing…`
                          : `${threadDetail.typing.map((u) => u.name).join(", ")} are typing…`}
                      </span>
                    </div>
                  </div>
                )}
              </div>
              {/* Composer */}
              <div className="p-3 border-t border-slate-200 flex items-end gap-2">
                <Textarea
                  value={body}
                  onChange={(e) => { setBody(e.target.value); if (e.target.value.trim()) notifyTyping(); }}
                  onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(); }}
                  rows={2}
                  maxLength={4000}
                  placeholder="Write a message… (⌘/Ctrl-Enter to send)"
                  className="bg-slate-50 border-slate-200 rounded-none resize-none flex-1"
                  data-testid="messages-composer"
                />
                <Button
                  type="button"
                  onClick={send}
                  disabled={sending || !body.trim()}
                  className="h-10 rounded-none bg-[#0D9488] hover:bg-[#0F766E] text-white uppercase tracking-widest text-xs font-semibold px-4"
                  data-testid="messages-send"
                >
                  <Send className="w-3.5 h-3.5 mr-1.5" /> {sending ? "Sending…" : "Send"}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Rename group thread dialog */}
      {renameOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setRenameOpen(false); }}
          data-testid="messages-rename-dialog"
        >
          <div className="bg-white border border-slate-200 max-w-md w-full">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <div>
                <div className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-slate-500">// Group name</div>
                <div className="font-display font-black uppercase tracking-tight text-lg">Name this conversation</div>
              </div>
              <button onClick={() => setRenameOpen(false)} className="text-slate-500 hover:text-slate-900" aria-label="Close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <Input
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                placeholder="e.g. Peptide Study Squad"
                maxLength={80}
                autoFocus
                className="bg-slate-50 border-slate-200 rounded-none h-10 text-sm"
                onKeyDown={(e) => { if (e.key === "Enter") submitRename(); }}
                data-testid="messages-rename-input"
              />
              <div className="text-[10px] font-mono-num uppercase tracking-widest text-slate-400">
                Leave empty to fall back to the auto-generated name.
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setRenameOpen(false)}
                  className="h-9 rounded-none border-slate-200 text-slate-700 uppercase tracking-widest text-[10px]"
                  data-testid="messages-rename-cancel"
                >Cancel</Button>
                <Button
                  type="button"
                  onClick={submitRename}
                  disabled={renameBusy}
                  className="h-9 rounded-none bg-[#0D9488] hover:bg-[#0F766E] text-white uppercase tracking-widest text-[10px] font-semibold px-4"
                  data-testid="messages-rename-submit"
                >{renameBusy ? "Saving…" : "Save"}</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Directory picker — new thread OR add-participant */}
      {showPicker && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) closePicker(); }}
          data-testid="messages-directory-picker"
        >
          <div className="bg-white border border-slate-200 max-w-md w-full max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <div>
                <div className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-slate-500">
                  {pickerMode === "add" ? "// Add someone to the conversation" : "// Start a conversation"}
                </div>
                <div className="font-display font-black uppercase tracking-tight text-lg">
                  {pickerMode === "add" ? "Pull in a member" : "Message any member"}
                </div>
              </div>
              <button onClick={closePicker} className="text-slate-500 hover:text-slate-900" aria-label="Close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-3 border-b border-slate-200">
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                <Input
                  value={pickerQuery}
                  onChange={(e) => setPickerQuery(e.target.value)}
                  placeholder="Search by name, handle, or email…"
                  className="bg-slate-50 border-slate-200 rounded-none h-9 pl-8 text-sm"
                  autoFocus
                  data-testid="messages-picker-search"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {pickerLoading ? (
                <div className="p-6 text-center text-slate-500 text-xs font-mono-num uppercase tracking-widest">Searching…</div>
              ) : pickerResults.length === 0 ? (
                <div className="p-6 text-center text-slate-500 text-sm">
                  {pickerQuery ? "No matches. Try a different search." : "No members available."}
                </div>
              ) : pickerResults.map((s) => (
                <button
                  key={s.id}
                  onClick={() => pickerMode === "add" ? addParticipant(s) : startWithMember(s)}
                  disabled={addingParticipant}
                  className="w-full flex items-center gap-3 p-3 border-b border-slate-200 hover:bg-slate-50 transition-colors text-left disabled:opacity-50"
                  data-testid={`messages-picker-item-${s.id}`}
                >
                  <UserBubble u={s} size={36} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="text-sm text-slate-900 font-medium truncate">{s.name}</div>
                      <RolePill role={s.role} />
                    </div>
                    <div className="text-xs text-slate-500 font-mono-num truncate">{s.handle ? `@${s.handle}` : s.email}</div>
                  </div>
                  {pickerMode === "add" && (
                    <UserPlus className="w-4 h-4 text-[#0D9488] shrink-0" />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
